import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    Tooltip as RechartsTooltip,
} from "recharts";
import { colors } from "../../theme/colors";
import { TooltipShell } from "../common/Tooltip";
import { buildLoadoutRadarAxes } from "../../utils/csSkillsState";
import ClientResponsiveContainer from "./ClientResponsiveContainer";

const CHART_NO_FOCUS_SX = {
    outline: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: "none" },
    "& .recharts-wrapper": { outline: "none !important" },
    "& .recharts-surface": { outline: "none !important" },
    "& svg": { outline: "none" },
};

function RadarAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-52} y={-10} width={104} height={22}>
                <Typography
                    component="div"
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: colors.text.secondary,
                        lineHeight: 1.2,
                        textAlign: "center",
                    }}
                >
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

function RadarTooltip({ active, payload }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload;
    if (!point) {
        return null;
    }

    return (
        <TooltipShell sx={{ minWidth: "7.5rem" }}>
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    lineHeight: 1,
                }}
            >
                {point.subject}
            </Typography>
            <Typography
                sx={{
                    mt: "0.35rem",
                    fontSize: "0.92rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {point.display}
            </Typography>
        </TooltipShell>
    );
}

export default function ProfileLoadoutRadar({ mapData }) {
    const axes = useMemo(() => buildLoadoutRadarAxes(mapData), [mapData]);
    const [isWideScreen, setIsWideScreen] = useState(false);

    useEffect(() => {
        const media = window.matchMedia("(min-width:2000px)");
        const syncWideScreen = () => setIsWideScreen(media.matches);
        syncWideScreen();
        media.addEventListener("change", syncWideScreen);
        return () => media.removeEventListener("change", syncWideScreen);
    }, []);

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: { xs: 0, sm: "12rem" },
                width: "100%",
                height: { xs: "14rem", sm: "12.5rem" },
                [`@media (min-width: 2000px)`]: {
                    minWidth: "16rem",
                    height: "16rem",
                },
                ...CHART_NO_FOCUS_SX,
            }}
        >
            <ClientResponsiveContainer
                minHeight={200}
                initialDimension={{ width: 280, height: 200 }}
            >
                <RadarChart
                    cx="50%"
                    cy="50%"
                    outerRadius={isWideScreen ? "74%" : "68%"}
                    data={axes}
                >
                    <PolarGrid
                        gridType="polygon"
                        stroke="rgba(255,255,255,0.14)"
                        radialLines
                    />
                    <PolarAngleAxis
                        dataKey="subject"
                        tick={RadarAxisTick}
                        tickLine={false}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        domain={[0, 10]}
                        ticks={[2, 4, 6, 8, 10]}
                        tick={false}
                        axisLine={false}
                    />
                    <RechartsTooltip content={<RadarTooltip />} />
                    <Radar
                        dataKey="value"
                        stroke={colors.primary.main}
                        fill={colors.primary.main}
                        fillOpacity={0.18}
                        strokeWidth={2}
                        isAnimationActive={false}
                        dot={{
                            r: 3.5,
                            fill: "transparent",
                            stroke: colors.primary.main,
                            strokeWidth: 1.75,
                        }}
                        activeDot={{
                            r: 4,
                            fill: "transparent",
                            stroke: colors.accent.main,
                            strokeWidth: 1.75,
                        }}
                    />
                </RadarChart>
            </ClientResponsiveContainer>
        </Box>
    );
}

ProfileLoadoutRadar.propTypes = {
    mapData: PropTypes.object,
};
